import { useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { LoadingBlock, PageHeader } from '@/components/PageHeader';
import { apiFetch } from '@/lib/api/client';
import { today } from '@/lib/format';
import type { DashboardSite } from '@/pages/safe/safeDashboardApi';
import { useSafeJournals } from '@/pages/safe/safeJournalApi';

import { STAGE_BADGE, feelsLikeTemp, heatSeasonStart, heatStage, useHeatZones, type HeatStage } from './heatApi';

/**
 * 현장 › 온열질환 전체 현황 — 오늘 예보 최고기온(대시보드 판정과 같은 값) · 최근 일일 점검 · 이번 폭염 기간(5.15~)의
 * 사고보고 수 · 구역 요약 · 체감온도 계산.
 */
export function SiteHeatStatusPage({ siteId }: { siteId: string }) {
  const navigate = useNavigate();
  const status = useQuery({
    queryKey: ['safe-site', siteId, 'status'],
    queryFn: () => apiFetch<DashboardSite | null>(`/v1/safe/sites/${siteId}/status`),
  });
  const season = heatSeasonStart(today());
  const checks = useSafeJournals('HEAT_CHECK', siteId, season, '');
  const accidents = useSafeJournals('HEAT_ACCIDENT', siteId, season, '');
  const zones = useHeatZones(siteId);

  if (status.isPending) return <LoadingBlock />;
  const site = status.data ?? null;
  const go = (path: string) => navigate(`/sites/${encodeURIComponent(siteId)}/${path}`);
  const recentChecks = [...(checks.data ?? [])].sort((a, b) => b.journalDate.localeCompare(a.journalDate)).slice(0, 8);
  const activeZones = (zones.data ?? []).filter((zone) => zone.active);

  return (
    <div className="space-y-5">
      <style>{STYLES}</style>
      <PageHeader
        title="온열질환 전체 현황"
        actions={
          <>
            <button type="button" className="btn-primary" onClick={() => go('heat-check?new=1')}>
              일일 점검 작성
            </button>
            <button type="button" className="btn-secondary" onClick={() => go('heat-accident?new=1')}>
              사고보고 작성
            </button>
          </>
        }
      />

      <section className="hs-tiles">
        <Tile label="오늘 예보 최고기온">
          {site?.maxTemp == null ? (
            '—'
          ) : (
            <>
              {site.maxTemp}℃
              {site.heat && <span className={`badge ${site.heat === '위험' ? 'badge-red' : 'badge-yellow'}`}>{site.heat}</span>}
            </>
          )}
        </Tile>
        <Tile label="최근 일일 점검">{recentChecks[0]?.journalDate ?? '—'}</Tile>
        <Tile label="사고보고 (5.15~)">{accidents.isPending ? '…' : `${accidents.data?.length ?? 0}건`}</Tile>
        <Tile label="사용 구역">{zones.isPending ? '…' : `${activeZones.length}곳`}</Tile>
      </section>

      <div className="hs-two">
        <section className="card hs-card">
          <h2>
            최근 일일 점검
            <button type="button" className="hs-more" onClick={() => go('heat-check')}>
              전체
            </button>
          </h2>
          {checks.isPending ? (
            <LoadingBlock />
          ) : recentChecks.length === 0 ? (
            <div className="hs-empty">점검 기록이 없습니다.</div>
          ) : (
            <ul className="hs-list">
              {recentChecks.map((row) => (
                <li key={row.id} onClick={() => go(`heat-check?open=${row.id}`)}>
                  <span style={{ width: '96px' }}>{row.journalDate}</span>
                  <span className="hs-grow">{stageBadge(row.title) ?? row.title ?? '—'}</span>
                  <span style={{ color: 'var(--text-muted)' }}>{row.inspector ?? row.authorName ?? ''}</span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="card hs-card">
          <h2>
            작업 구역
            <button type="button" className="hs-more" onClick={() => go('heat-zones')}>
              설정
            </button>
          </h2>
          {zones.isPending ? (
            <LoadingBlock />
          ) : activeZones.length === 0 ? (
            <div className="hs-empty">등록된 구역이 없습니다.</div>
          ) : (
            <ul className="hs-list">
              {activeZones.map((zone) => (
                <li key={zone.id} onClick={() => go('heat-zones')}>
                  <span className="hs-grow" style={{ fontWeight: 600 }}>
                    {zone.name}
                  </span>
                  <span className="badge badge-gray">{zone.outdoor ? '실외' : '실내'}</span>
                  <span style={{ color: zone.hasShade ? undefined : 'var(--text-muted)' }}>그늘막 {zone.hasShade ? '○' : '×'}</span>
                  <span style={{ color: zone.hasRestArea ? undefined : 'var(--text-muted)' }}>휴게 {zone.hasRestArea ? '○' : '×'}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <FeelsLikeCalculator />
    </div>
  );
}

function stageBadge(title: string | null): ReactNode {
  if (!title || !(title in STAGE_BADGE)) return null;
  return <span className={`badge ${STAGE_BADGE[title as HeatStage]}`}>{title}</span>;
}

function Tile({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="card hs-tile">
      <span className="hs-label">{label}</span>
      <span className="hs-value">{children}</span>
    </div>
  );
}

/** 기온 · 습도 → 체감온도 · 폭염 단계. */
function FeelsLikeCalculator() {
  const [temp, setTemp] = useState('');
  const [humidity, setHumidity] = useState('');
  const t = Number(temp);
  const rh = Number(humidity);
  const ready = temp.trim() !== '' && humidity.trim() !== '' && Number.isFinite(t) && Number.isFinite(rh) && rh >= 0 && rh <= 100;
  const feels = ready ? feelsLikeTemp(t, rh) : null;
  const stage = feels === null ? null : heatStage(feels);

  return (
    <section className="card hs-card">
      <h2>체감온도 계산</h2>
      <div className="hs-calc">
        <label>
          기온(℃)
          <input className="input" type="number" inputMode="decimal" value={temp} onChange={(e) => setTemp(e.target.value)} />
        </label>
        <label>
          습도(%)
          <input
            className="input"
            type="number"
            inputMode="decimal"
            min={0}
            max={100}
            value={humidity}
            onChange={(e) => setHumidity(e.target.value)}
          />
        </label>
        <div className="hs-result">
          <span className="hs-label">체감온도</span>
          <span className="hs-value">
            {feels === null ? '—' : `${feels}℃`}
            {stage && <span className={`badge ${STAGE_BADGE[stage]}`}>{stage}</span>}
          </span>
        </div>
      </div>
    </section>
  );
}

const STYLES = `
.hs-tiles { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px; }
.hs-tile { display: flex; flex-direction: column; gap: 6px; padding: 14px 16px; border-radius: 12px; }
.hs-label { font-size: 12px; color: var(--text-secondary); }
.hs-value { display: flex; align-items: center; gap: 8px; font-size: 20px; font-weight: 700; }
.hs-card { padding: 18px 20px; border-radius: 14px; }
.hs-card h2 { display: flex; align-items: center; justify-content: space-between; font-size: 15px; font-weight: 700; margin: 0 0 14px; }
.hs-more { background: none; border: none; font-size: 12px; font-weight: 400; color: var(--text-secondary); cursor: pointer; }
.hs-two { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px; }
.hs-list { list-style: none; margin: 0; padding: 0; }
.hs-list li { display: flex; align-items: center; gap: 10px; padding: 9px 4px; font-size: 13px; border-bottom: 1px solid var(--border); cursor: pointer; }
.hs-list li:last-child { border-bottom: none; }
.hs-list li:hover { background: var(--bg-hover, #f8fafc); }
.hs-grow { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.hs-empty { font-size: 13px; color: var(--text-muted); padding: 8px 4px; }
.hs-calc { display: flex; flex-wrap: wrap; align-items: flex-end; gap: 14px; }
.hs-calc label { display: flex; flex-direction: column; gap: 6px; font-size: 12px; font-weight: 600; color: var(--text-secondary); width: 140px; }
.hs-result { display: flex; flex-direction: column; gap: 6px; padding-bottom: 4px; }
@media (max-width: 900px) {
  .hs-tiles { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .hs-two { grid-template-columns: minmax(0, 1fr); }
}
`;
